//
// AHT Weight Management — Oral Semaglutide (Wegovy® tablets) Initial Supply Pathway
// Source: AHT_Oral_Wegovy_Supply_Pathway_v1_0.docx
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// This pathway covers initial supply only: 1 × Wegovy® 1.5 mg tablets, 30-tablet pack.

import type { Pathway } from "../../types/pathway.types";
import {
  OSEM_ORAL_SUITABILITY_SWALLOW,
  OSEM_OUT_CANNOT_SUPPLY,
  oralSemaglutideScreeningDownstreamNodes,
  oralSemaglutideScreeningOutcomes,
} from "./_shared/oral-semaglutide-screening-downstream";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const CONFIRM_CARD = "osem-init-confirmation-card";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtOralSemaglutideInitialPathway: Pathway = {
  id: "aht-wt-oral-semaglutide-initial",
  name: "Oral Semaglutide (Wegovy® tablets) — Initial Supply",
  population: "Adults attending for initial supply of oral semaglutide",
  pgdRefs: ["pgd-aht-wt-oral-semaglutide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: CONFIRM_CARD,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Confirm answers carried over from the selection pathway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: CONFIRM_CARD,
      type: "confirmation_card",
      heading: "Confirm answers from drug selection",
      items: [
        // Q3 items — thyroid/pancreatitis/T1DM same as injectable sema
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q3_thyroid",
          questionText: "Personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN2)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q4_pancreatitis",
          questionText: "History of pancreatitis (acute or chronic)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q5_t1dm_t3c",
          questionText: "Type 1 diabetes (T1DM) or Type 3c (pancreatogenic) diabetes?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // Renal — DIVERGENCE DV4: threshold is eGFR <30, not CrCl <15.
        // Pulls from the new oral_sema_egfr_below_30 Q7 item (added in
        // weight-selection.pathway.ts). If not yet answered (notFound),
        // pharmacist must answer here — this is intentional per the supply
        // pathway which states "ask afresh" because the CrCl <15 check
        // does not clear the eGFR <30 threshold.
        {
          sourceNodeId: "Q7_severe_renal_or_hepatic_impairment",
          sourceItemId: "oral_sema_egfr_below_30",
          questionText: "Renal impairment with eGFR below 30 mL/min/1.73m²? (Verify eGFR — the shared selection pathway used CrCl <15, which does not clear this threshold for oral semaglutide.)",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // Hepatic — same as injectable
        {
          sourceNodeId: "Q7_severe_renal_or_hepatic_impairment",
          sourceItemId: "severe_hepatic",
          questionText: "Severe hepatic impairment (Child-Pugh C)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // GI — same as injectable
        {
          sourceNodeId: "Q14_severe_gi_or_gastroparesis",
          sourceItemId: "q14_severe_gi",
          questionText: "Severe or unstable gastrointestinal disease, including gastroparesis?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // Pregnancy — same as injectable
        {
          sourceNodeId: "Q15_pregnancy_breastfeeding_planning",
          questionText: "Pregnant, breastfeeding, or planning pregnancy within the next 2 months? (Semaglutide must be discontinued at least 2 months before a planned pregnancy.)",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // Retinopathy — DIVERGENCE DV5: any stage, not just proliferative.
        // Pulls from the new q18_retinopathy_any Q17-19 item (added in
        // weight-selection.pathway.ts). If not yet answered (notFound),
        // pharmacist must answer here.
        {
          sourceNodeId: "Q17_ciclosporin",
          sourceItemId: "q18_retinopathy_any",
          questionText: "Diabetic retinopathy (any stage, including non-proliferative) or macular oedema? Oral semaglutide is excluded at any stage — this is broader than the injectable threshold.",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
        // Hypersensitivity — no prior answer in selection flow; always fresh.
        // sourceNodeId deliberately does not match any pathway node so the
        // confirmation card always resolves this as "no previous answer
        // found" and requires the pharmacist to answer directly.
        {
          sourceNodeId: "osem-init-fresh-hypersensitivity",
          questionText: "Previous anaphylactic reaction to semaglutide, any GLP-1 receptor agonist, or any incretin mimetic; or known hypersensitivity to semaglutide or any tablet excipient (salcaprozate sodium or magnesium stearate)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OSEM_OUT_CANNOT_SUPPLY },
        },
      ],
      next: OSEM_ORAL_SUITABILITY_SWALLOW,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Oral suitability screening + Steps 2b–5 — shared screening downstream
    // (PGD fresh checks, interaction checks, cautions review, supply
    // attestation). Single source of truth.
    // ─────────────────────────────────────────────────────────────────────────
    ...oralSemaglutideScreeningDownstreamNodes,
  ],

  outcomes: oralSemaglutideScreeningOutcomes,
};
